<template>
  <div>
    <v-card flat height="100%">
      <v-toolbar flat color="grey lighten-2" height="50px">
        <v-toolbar-title
          flat
          class="black--text body-2 font-weight-bold pa-0"
          >{{ $t("venue.save.form.configuration.label") }}</v-toolbar-title
        >
        <v-spacer></v-spacer>
        <v-btn small icon @click="$emit('closeConfig')">
          <v-icon color="error lighten-2">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-stepper
        v-if="local_venue"
        v-model="config_stepper"
        vertical
        class="ma-0 pa-0"
      >
        <!-- Venue Configuration -->
        <v-stepper-step editable step="1">
          {{ $t("venue.label.update.seats_config") }}
          <v-chip
            v-if="local_venue.is_published"
            class="mx-2"
            small
            :color="'secondary'"
            outline
            >{{ $t("venue.status.locked")}}</v-chip
          >
        </v-stepper-step>
        <v-stepper-content step="1">
          <v-form ref="seats_management">
            <v-text-field
              id="rows"
              :label="$t('venue.form.label.rows')"
              name="rows"
              :rules="[
                rules.required,
                rules.numeric,
                rules.inBetween(1, 50, local_venue.rows),
              ]"
              color="primary"
              prepend-icon="$vuetify.icons.rms_agreement"
              type="number"
              v-model="local_venue.rows"
              :disabled="local_venue.is_published == 1"
            ></v-text-field>
            <v-text-field
              id="columns"
              :label="$t('venue.form.label.columns')"
              name="columns"
              :rules="[
                rules.required,
                rules.numeric,
                rules.inBetween(1, 50, local_venue.columns),
              ]"
              color="primary"
              prepend-icon="$vuetify.icons.rms_agreement"
              type="number"
              v-model="local_venue.columns"
              :disabled="local_venue.is_published == 1"
            ></v-text-field>
            <v-select
              class="pt-2"
              v-model="local_venue.label_type_row"
              :items="DEFINES.SEAT_LABELLING_TYPES_ARRAY"
              :label="$t('venue.form.label.label_type_row')"
              item-text="text"
              item-value="value"
              hide-details
              color="saSecondary"
              prepend-icon="$vuetify.icons.rms_agreement"
              :disabled="local_venue.is_published == 1"
            ></v-select>
            <v-select
              class="pt-3"
              v-model="local_venue.label_type_column"
              :items="DEFINES.SEAT_LABELLING_TYPES_ARRAY"
              :label="$t('venue.form.label.label_type_column')"
              item-text="text"
              item-value="value"
              hide-details
              color="saSecondary"
              prepend-icon="$vuetify.icons.rms_agreement"
              :disabled="local_venue.is_published == 1"
            ></v-select>
            <v-checkbox
              class="pt-2"
              hide-details
              :label="$t('venue.form.label.is_label_row_reversed')"
              v-model="local_venue.is_label_row_reversed"
              :disabled="local_venue.is_published == 1"
            ></v-checkbox>
            <v-checkbox
              class="pt-2"
              hide-details
              :label="$t('venue.form.label.is_label_column_reversed')"
              v-model="local_venue.is_label_column_reversed"
              :disabled="local_venue.is_published == 1"
            ></v-checkbox>
          </v-form>
          <div class="mt-3">
            <v-btn
              color="secondary"
              :disabled="local_venue.is_published == 1"
              @click="update('seats_management')"
              >{{ $t("venue.save.from.btn_save") }}</v-btn
            >
            <v-btn
              color="secondary"
              :disabled="local_venue.is_published == 1"
              @click="update('seats_management', 2)"
              >{{ $t("venue.save.from.btn_save_and_next") }}</v-btn
            >
          </div>
        </v-stepper-content>
        <!-- Manage Basic Details -->
        <v-stepper-step editable step="2">{{
          $t("venue.save.form.basic_details.label")
        }}</v-stepper-step>
        <v-stepper-content step="2">
          <v-form ref="basic_details">
            <v-text-field
              id="name"
              :label="$t('venue.form.label.name')"
              name="name"
              :rules="[
                rules.required,
                rules.min_length(3),
                rules.max_length(250),
              ]"
              counter="250"
              color="primary"
              prepend-icon="$vuetify.icons.rms_agreement"
              type="text"
              v-model="local_venue.name"
            ></v-text-field>
            <v-text-field
              id="subtitle"
              :label="$t('venue.form.label.subtitle')"
              name="subtitle"
              counter="250"
              color="primary"
              prepend-icon="$vuetify.icons.rms_agreement"
              type="text"
              v-model="local_venue.subtitle"
            ></v-text-field>
            <v-text-field
              id="address"
              :label="$t('venue.form.label.address')"
              name="address"
              :rules="[
                rules.required,
                rules.min_length(3),
                rules.max_length(250),
              ]"
              color="primary"
              prepend-icon="$vuetify.icons.rms_agreement"
              type="text"
              v-model="local_venue.address"
            ></v-text-field>
            <v-tooltip v-model="showNavigationTooltip" top>
              <template v-slot:activator="{ on }">
                <v-text-field
                  id="navigation_link"
                  :label="$t('venue.form.label.navigation_link')"
                  name="navigation_link"
                  :rules="[rules.min_length(3), rules.max_length(250)]"
                  counter="250"
                  color="primary"
                  prepend-icon="$vuetify.icons.rms_agreement"
                  type="text"
                  v-model="local_venue.navigation_link"
                  append-icon="info"
                  @click:append="showNavigationTooltip = !showNavigationTooltip"
                >
                </v-text-field>
              </template>
              <span>{{ $t("venue.save.form.navigation_link_tooltip") }}</span>
            </v-tooltip>
            <v-textarea
              clearable
              clear-icon="mdi-close-circle"
              :label="$t('venue.form.label.notes')"
              rows="1"
              row-height="15"
              auto-grow
              prepend-icon="$vuetify.icons.rms_agreement"
              :value="local_venue.notes"
              v-model="local_venue.notes"
            ></v-textarea>
          </v-form>
          <div>
            <v-btn color="secondary" @click="update('basic_details')">{{
              $t("venue.save.from.btn_save")
            }}</v-btn>
            <v-btn
              color="secondary"
              @click="update('basic_details', 3)"
              v-if="local_venue.is_published != 1"
              >{{ $t("venue.save.from.btn_save_and_next") }}</v-btn
            >
          </div>
        </v-stepper-content>
        <!-- Manage Publish -->
        <v-stepper-step v-if="local_venue.is_published != 1" editable step="3">
          {{ $t("venue.save.from.publish_label") }}
        </v-stepper-step>
        <v-stepper-content v-if="local_venue.is_published != 1" step="3">
          <v-card flat height="100%">
            <!-- Tip 1 -->
            <div>
              <v-icon size="8" class="mb-1"
                >$vuetify.icons.rms_blank_circle</v-icon
              >
              {{ $t("venue.save.form.published.tip_1") }}
            </div>
            <!-- Tip 1 -->
            <div class="mt-2">
              <v-icon size="8" class="mb-1"
                >$vuetify.icons.rms_blank_circle</v-icon
              >
              {{ $t("venue.save.form.published.tip_2") }}
            </div>
          </v-card>
          <v-btn
            color="secondary"
            v-if="local_venue"
            class="my-3 ml-0"
            @click="publishConfirmationDialog = true"
            :disabled="local_venue.is_published == 1"
            >{{ $t("venue.save.from.publish_label") }}</v-btn
          >
        </v-stepper-content>
      </v-stepper>
    </v-card>
    <ConfirmationDialog
      :text="$t('venue.form.publishing.confirm_text')"
      v-if="publishConfirmationDialog"
      :dialog="publishConfirmationDialog"
      @confirm="markPublished(true)"
      @cancel="markPublished(false)"
    ></ConfirmationDialog>
  </div>
</template>
<script>
import rules from "@/validation";
import ConfirmationDialog from "@/components/dialogBox/Confirmation";

export default {
  name: "VenueConfig",
  props: {
    venue: {
      type: Object,
      default: null,
    },
    publishConfirmationDialog: false,
  },
  components: {
    ConfirmationDialog,
  },
  data() {
    return {
      rules,
      local_venue: null,
      showNavigationTooltip: false,
      config_stepper: 1,
    };
  },

  watch: {
    venue: {
      deep: true,
      handler: function () {
        if (!this.venue) {
          return;
        }
        this.local_venue = Object.assign({}, this.venue);
        if (this.local_venue.is_published) {
          this.config_stepper = 2;
        }
      },
    },
  },
  methods: {
    async update(validateStep = "", nextStep = null) {
      try {
        // Step 1 - Validate seats_management
        if (
          validateStep == "seats_management" &&
          !this.$refs.seats_management.validate()
        ) {
          return false;
        }
        // Step 1 - Validate basic_details
        if (
          validateStep == "basic_details" &&
          !this.$refs.basic_details.validate()
        ) {
          return false;
        }

        var venue = Object.assign({}, this.local_venue);

        // if (venue.label_type_column == venue.label_type_row) {
        //   this.$store.dispatch('app/showMessage', {
        //     message: this.$t(
        //       'venue.save.error.row_and_column_labelling_can_not_same'
        //     ),
        //     color: 'error',
        //   });
        //   return;
        // }

        let response = await this.$store.dispatch("venue/update", venue);
        this.$emit("venueUpdated", response.venue);
        if (nextStep) {
          this.config_stepper = nextStep;
        }
      } catch (error) {
        this.$store.dispatch("app/showMessage", {
          message: error.message,
          color: "error",
        });
      }
    },

    publish() {},
    markPublished(value) {
      if (value) {
        this.local_venue.is_published = 1;
        this.publishConfirmationDialog = false;
        this.update();
      } else {
        this.local_venue.is_published = 0;
        this.publishConfirmationDialog = false;
      }
    },
  },
};
</script>
